import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useAppContext } from '../../AppContext';
import { Panels } from '../Consts';
import { Button, FormItem, FormLayoutGroup, Panel, PullToRefresh } from '@vkontakte/vkui';
import { UserSelectComponent } from './components/UserSelectComponent';
import { AmountInputComponent } from './components/AmountInputComponent';
import { CommentInputComponent } from './components/CommentInputComponent';
import { DateInputComponent } from './components/DateInputComponent';
import { clearCache as eventInfoCacheClear } from '../Events/ShowEventPanel';
import { ErrorLoadComponent } from '../components/ErrorLoadComponent';
import { getResponseFromError } from '../../helpers/helpers';
import { MAX_AMOUNT, MIN_AMOUNT } from '../../Consts';
import { ImageUploadComponent } from './components/ImageUploadComponent';
import { SelectImagesDeleteComponent } from '../components/SelectImagesDeleteComponent';
import { PlatformPanelHeader } from '../components/PlatformPanelHeader';
import { useAppStore } from '../../AppStore';

const CURRENT_PANEL = Panels.CREATE_REPAYMENT_PANEL;

const CreateRepaymentPanel = (props) => {
  const context = useAppContext();
  const appStore = useAppStore();
  const params = context.router.getParams();

  const [userFrom, setUserFrom] = useState(0);
  const [userFromError, setUserFromError] = useState('');
  const [userToError, setUserToError] = useState('');
  const [userTo, setUserTo] = useState(0);
  const [amount, setAmount] = useState(0);
  const [amountErrorText, setAmountErrorText] = useState('');
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState('');
  const [dateTime, setDateTime] = useState('');
  const [dateError, setDateError] = useState('');
  const [images, setImages] = useState([]);
  const [imagesError, setImagesError] = useState('');
  const [send, setSend] = useState(false);
  const [users, setUsers] = useState([]);
  const [errorInit, setErrorInit] = useState(null);
  const [prices, setPrices] = useState({});

  const isFormSavedRef = useRef(false);

  const fetch = (spinner = true) => {
    if (spinner) {
      context.spinner(true);
    }
    (new Promise((resolve, reject) => {
      context.init().then(() => {
        context.httpClient().getEventV3(params.eventId)
          .then((response) => {
            const prices = {};
            (response.data.credits || []).map((creditor) => {
              (creditor.users || []).map((user) => {
                prices[creditor.user_id + '_' + user.user_id] = user.amount;
              });
            });

            resolve({
              prices,
              users: [...response.data.users],
            });
          })
          .catch((error) => {
            reject(error);
          })
      }).catch((error) => {
        reject(error);
      });
    }))
    .then(({users, prices}) => {
        setDateTime(context.lang().dateFormatToInput(new Date()));
        const fromId = params.fromId || 0;
        const toId = params.toId || 0;

        setUserFrom(fromId);
        setUserTo(toId);
        setUsers(users);
        setPrices(prices);
        setAmount(prices[toId + '_' + fromId] || 0);

        context.spinner(false);
        setErrorInit(null);
    })
    .catch((error) => {
        context.spinner(false);
        setErrorInit(error);
    });
  }

  useEffect(() => {
    fetch();
  }, []);

  useEffect(() => {
    setAmount(prices[userTo + '_' + userFrom] || 0);
  }, [userFrom, userTo]);

  const save = (eventId, from, to, amount, date, comment, images) => {
    if (isFormSavedRef.current) {
      return;
    }

    isFormSavedRef.current = true;
    setSend(true);
    context.spinner(true);
    context.httpClient()
      .createRepaymentV4(eventId, from, to, amount, date, comment, images.map((img) => img.id))
      .then((response) => {
        eventInfoCacheClear(eventId, context.cache);
        isFormSavedRef.current = false;
        setSend(false);
        context.spinner(false);
        context.back();
      })
      .catch((error) => {
        isFormSavedRef.current = false;
        setSend(false);
        context.spinner(false);
        const response = getResponseFromError(error);
        if (!response) {
          context.showError(error);
          return;
        }

        setUserToError(response.to || '');
        delete response['to'];

        setUserFromError(response.from || '');
        delete response['from'];

        setAmountErrorText(response.amount || '');
        delete response['amount'];

        setDateError(response.date || '');
        delete response['date'];

        setCommentError(response.comment || '');
        delete response['comment'];

        const imagesErrors = [];
        Object.keys(response).forEach((key) => {
          const [name, index] = key.split('.', 2);
          switch (name) {
            case 'images':
              imagesErrors.push(response[key] || '');
              delete response[key];
              break;
          }
        });

        setImagesError(imagesErrors.join(','));

        if (response.message) {
          context.showError(null, response.message);
        } else if (Object.keys(response).length > 0) {
          context.showError(error);
        }
      });

    context.getBridge().retargeting.createRepayment(appStore.currentUserId);
  }

  const onSaveHandler = () => {
    save(params.eventId, userFrom, userTo, amount, dateTime, comment, images);
  }

  const usersPaymentInfo = useMemo(() => {
    const result = {};
    (users || []).forEach((user) => {
      result[user.id] = user.payment_info || '';
    });

    return result;
  }, [users]);
  const usersForSelectMembers = users.map((user) => ({
    value: user.id,
    label: user.name,
    img: user.img,
    type: user.type,
  }));

  if (errorInit) {
    return (<ErrorLoadComponent
      error={errorInit}
      onFetch={fetch}
    />)
  }

  return (<Panel id={CURRENT_PANEL}>
    <PlatformPanelHeader
      text={context.lang().getKey(CURRENT_PANEL, 'header')}
    />
    {context.snackbar}
    <form>
      <FormLayoutGroup mode="vertical">
        <PullToRefresh onRefresh={() => fetch(false)}>
          <UserSelectComponent
            label={context.lang().getKey(CURRENT_PANEL, 'user_from')}
            placeholder={context.lang().getKey(CURRENT_PANEL, 'user_from_placeholder')}
            error={userFromError}
            setError={setUserFromError}
            members={usersForSelectMembers.filter((item) => item.value !== userTo)}
            value={userFrom}
            setValue={setUserFrom}
          />
          <UserSelectComponent
            label={context.lang().getKey(CURRENT_PANEL, 'user_to')}
            placeholder={context.lang().getKey(CURRENT_PANEL, 'user_to_placeholder')}
            error={userToError}
            setError={setUserToError}
            members={usersForSelectMembers.filter((item) => item.value !== userFrom)}
            value={userTo}
            setValue={setUserTo}
            description={usersPaymentInfo[userTo] || ''}
          />
          <AmountInputComponent
            error={amountErrorText}
            setError={setAmountErrorText}
            amount={amount}
            setAmount={setAmount}
            min={MIN_AMOUNT}
            max={MAX_AMOUNT}
            label={context.lang().getKey('app', 'amount_input_label')}
            errorTextMin={context.lang().getKey('errors', 'amount_error_min')}
            errorTextMax={context.lang().getKey('errors', 'amount_error_max')}
          />
          <DateInputComponent
            defaultDate={context.lang().dateFormatToInput(new Date())}
            error={dateError}
            setError={setDateError}
            dateTime={dateTime}
            setDateTime={setDateTime}
            label={context.lang().getKey('app', 'date_input_label')}
            errorTextMinValue={context.lang().getKey('errors', 'date_error_min')}
            errorTextMaxValue={context.lang().getKey('errors', 'date_error_max')}
          />
          <CommentInputComponent
            error={commentError}
            setError={setCommentError}
            comment={comment}
            setComment={setComment}
            label={context.lang().getKey('app', 'comment_input_label')}
            errorTextMaxLength={context.lang().getKey('errors', 'comment_error_max_length_255_char')}
          />
          <SelectImagesDeleteComponent
            value={images}
            setValue={setImages}
          />
          <ImageUploadComponent
            eventId={params.eventId}
            value={images}
            setValue={setImages}
            error={imagesError}
            setError={setImagesError}
            label={context.lang().getKey('app', 'image_input_label')}
            errorTextMaxSize={context.lang().getKey('errors', 'image_error_max_size')}
            errorTextType={context.lang().getKey('errors', 'image_error_type')}
            errorMaxCount={context.lang().getKey('errors', 'image_error_max_count')}
            buttonLabel={context.lang().getKey('app', 'image_button_label__empty')}
            buttonLabelSelect={context.lang().getKey('app', 'image_button_label__selected')}
            maxSize={context.store.maxFileSize}
          />
        </PullToRefresh>
      </FormLayoutGroup>
      <FormLayoutGroup>
        <FormItem>
          <Button
            size="l"
            stretched={true}
            loading={send}
            disabled={send || !amount || amount < MIN_AMOUNT || amount > MAX_AMOUNT || userFrom === userTo}
            onClick={() => onSaveHandler()}
          >
            {context.lang().getKey(CURRENT_PANEL, 'button_save_label')}
          </Button>
        </FormItem>
      </FormLayoutGroup>
    </form>
  </Panel>);
}
export default CreateRepaymentPanel;
